/**
 * `<ExtractionQueuePanel>` — the D-006 per-stage extraction-queue status (frontend.md §2.1,
 * §3.3).
 *
 * D-006 phases extraction into Stage A (classical) / C (LLM) / D (corpus-global). frontend.md
 * §3.3 requires the queue status broken down PER STAGE — `ExtractionQueueStatus` carries
 * `stageA` / `stageC` / `stageD`, each a `StageQueueCounts` (queued/running/done/failed/
 * skipped). This panel renders that breakdown so the user can see "Stage A: 12 queued,
 * Stage C: 3 failed" rather than one opaque number.
 *
 * Polls on the capability-tiered interval (`useExtractionQueueQuery`), paused when the
 * Health surface is not the active route.
 */

import type { StageQueueCounts } from '@/types';
import { useExtractionQueueQuery } from '@/hooks';
import { LoadingState, EmptyState } from '@/components';
import { HealthCard } from './HealthCard';

interface ExtractionQueuePanelProps {
  /** True when Corpus Health is the active route — polling pauses otherwise. */
  active: boolean;
}

function StageRow({ stage, counts }: { stage: string; counts: StageQueueCounts }): JSX.Element {
  return (
    <div className="col" style={{ marginBottom: 12 }}>
      <strong>Stage {stage}</strong>
      <div className="stat-grid">
        <div className="stat">
          <div className="stat__value">{counts.queued}</div>
          <div className="stat__label">queued</div>
        </div>
        <div className="stat stat--running">
          <div className="stat__value">{counts.running}</div>
          <div className="stat__label">running</div>
        </div>
        <div className="stat">
          <div className="stat__value">{counts.done}</div>
          <div className="stat__label">done</div>
        </div>
        <div className="stat stat--failed">
          <div className="stat__value">{counts.failed}</div>
          <div className="stat__label">failed</div>
        </div>
        <div className="stat">
          <div className="stat__value">{counts.skipped}</div>
          <div className="stat__label">skipped</div>
        </div>
      </div>
    </div>
  );
}

export function ExtractionQueuePanel({ active }: ExtractionQueuePanelProps): JSX.Element {
  const queueQuery = useExtractionQueueQuery({ enabled: active });

  return (
    <HealthCard title="Extraction queue" anchorId="extraction-queue">
      {queueQuery.isLoading && <LoadingState label="Loading queue status…" />}
      {queueQuery.isError && (
        <EmptyState title="Could not load the extraction queue" detail="See the notification." />
      )}
      {queueQuery.data !== undefined && (
        <div>
          <StageRow stage="A — classical" counts={queueQuery.data.stageA} />
          <StageRow stage="C — LLM" counts={queueQuery.data.stageC} />
          {/* Stage D (corpus-global) is permanently unwired — owner won't-fix (F-04).
              Rendering an eternal all-zeros row read as breakage, so it's hidden
              (U7 review 2026-07-05). `stageD` stays on the wire type untouched. */}
        </div>
      )}
    </HealthCard>
  );
}
